<script setup lang="ts">
import { computed, nextTick, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  addBomItem,
  deleteWorkPack,
  getDownloadUrl,
  getProject,
  getWorkPack,
  getWorkPackHistory,
  listWorkPacks,
  listWorkPackTemplates,
  pushEvents,
  removeBomItem,
  uploadFile,
  WORK_PACK_STATUS_LABEL,
  type WorkPackStatus,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import WorkPackBriefingLink from "@/components/WorkPackBriefingLink.vue";
import WorkPackConstructionBrief from "@/components/WorkPackConstructionBrief.vue";
import WorkPackControl from "@/components/WorkPackControl.vue";
import WorkPackLocation from "@/components/WorkPackLocation.vue";
import WorkPackTaskBrief from "@/components/WorkPackTaskBrief.vue";
import {
  DOC_KINDS,
  kindLabel,
  readiness,
  SECTIONS,
  sectionDocs,
  sectionForKind,
  type PackSection,
} from "@/lib/workPackSections";

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id as string);
const queryClient = useQueryClient();

const { data: pack, isPending, isError, error } = useQuery({
  queryKey: ["work-pack", id],
  queryFn: () => getWorkPack(id.value),
});
const { data: templates } = useQuery({
  queryKey: ["work-pack-templates"],
  queryFn: () => listWorkPackTemplates(),
});

const { data: history } = useQuery({
  queryKey: ["work-pack-history", id],
  queryFn: () => getWorkPackHistory(id.value),
});

// Context for auto-populating the construction brief (project details + the
// project's other packs, for "copy from previous").
const projectId = computed(() => pack.value?.project_id);
const { data: project } = useQuery({
  queryKey: ["project", projectId],
  queryFn: () => getProject(projectId.value as string),
  enabled: () => !!projectId.value,
});
const { data: priorPacks } = useQuery({
  queryKey: ["work-packs", projectId],
  queryFn: () => listWorkPacks(projectId.value),
  enabled: () => !!projectId.value,
});

const refresh = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: ["work-pack", id] })
    .then(() => queryClient.invalidateQueries({ queryKey: ["work-packs"] }))
    .then(() =>
      queryClient.invalidateQueries({ queryKey: ["work-pack-history", id] }),
    );

const ready = computed(() =>
  pack.value ? readiness(pack.value, templates.value) : null,
);

function fmt(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";
}

function fmtDateTime(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";
}

// "Name · date" attribution, or null if there's nothing to show.
function attribution(
  name: string | null | undefined,
  at: string | null | undefined,
): string | null {
  if (!name && !at) return null;
  return [name || "—", at ? fmtDateTime(at) : null].filter(Boolean).join(" · ");
}

// --- add a document to a section ---
const activeSection = ref<string | null>(null);
const docForm = reactive({ kind: "", title: "", url: "", location: "", file: null as File | null });
const docBusy = ref(false);
const docError = ref<string | null>(null);

// Reveal the add form for a section — the form is rendered inside that section
// (which may be well down the page), so scroll it into view. Without this,
// clicking "Add" (e.g. RAMS from the top checklist) looks like nothing happened.
function scrollToSection(key: string): void {
  void nextTick(() => {
    document
      .getElementById(`wp-sec-${key}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
}

function openAdd(section: PackSection): void {
  activeSection.value = section.key;
  docError.value = null;
  docForm.kind = section.kinds[0];
  docForm.title = "";
  docForm.url = "";
  docForm.location = "";
  docForm.file = null;
  scrollToSection(section.key);
}

function onFile(e: Event): void {
  docForm.file = (e.target as HTMLInputElement).files?.[0] ?? null;
}

function kindsFor(section: PackSection) {
  return DOC_KINDS.filter((k) => section.kinds.includes(k.value));
}

// Open the add form for a specific document kind (from the template checklist),
// in the section that owns that kind, with the kind preselected.
function openAddForKind(kind: string): void {
  const s = sectionForKind(kind);
  if (!s) return;
  activeSection.value = s.key;
  docError.value = null;
  docForm.kind = kind;
  docForm.title = "";
  docForm.url = "";
  docForm.location = "";
  docForm.file = null;
  scrollToSection(s.key);
}

async function attach(): Promise<void> {
  if (!docForm.title.trim()) return;
  docBusy.value = true;
  docError.value = null;
  try {
    const fileId = docForm.file
      ? await uploadFile(docForm.file, "work_pack_document")
      : undefined;
    await pushEvents([
      {
        aggregate_type: "work_pack",
        aggregate_id: id.value,
        event_type: "WorkPackDocumentAttached",
        payload: {
          document_id: crypto.randomUUID(),
          kind: docForm.kind,
          title: docForm.title.trim(),
          location_ref: docForm.location.trim() || undefined,
          file_id: fileId,
          url: docForm.url.trim() || undefined,
          attached_at: new Date().toISOString(),
        },
      },
    ]);
    activeSection.value = null;
    await refresh();
  } catch (e) {
    docError.value = e instanceof Error ? e.message : String(e);
  } finally {
    docBusy.value = false;
  }
}

async function viewDocument(fileId: string): Promise<void> {
  window.open(await getDownloadUrl(fileId), "_blank");
}

// Draft the construction brief from a document tile: scroll to the brief and
// trigger its "read document -> draft" against that document.
const briefRef = ref<{ draftFrom: (id: string) => Promise<void> } | null>(null);
async function draftBriefFrom(documentId: string): Promise<void> {
  document
    .getElementById("construction-brief")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
  await briefRef.value?.draftFrom(documentId);
}

// --- structured Bill of Materials ---
// quantity binds a type="number" input, so v-model yields a number (or "" when
// blank) — keep the type broad and coerce to string before trimming.
const bomForm = reactive<{
  description: string;
  quantity: string | number;
  unit: string;
}>({ description: "", quantity: "", unit: "" });
const bomBusy = ref(false);
const bomError = ref<string | null>(null);

async function addLine(): Promise<void> {
  if (!bomForm.description.trim()) return;
  bomBusy.value = true;
  bomError.value = null;
  try {
    const qty = String(bomForm.quantity ?? "").trim();
    await addBomItem(id.value, {
      description: bomForm.description.trim(),
      quantity: qty === "" ? null : Number(qty),
      unit: bomForm.unit.trim() || null,
    });
    bomForm.description = "";
    bomForm.quantity = "";
    bomForm.unit = "";
    await refresh();
  } catch (e) {
    bomError.value = e instanceof Error ? e.message : String(e);
  } finally {
    bomBusy.value = false;
  }
}

async function removeLine(itemId: string): Promise<void> {
  await removeBomItem(id.value, itemId);
  await refresh();
}

// --- delete (soft) ---
const deleting = ref(false);
async function deletePack(): Promise<void> {
  if (!pack.value) return;
  const warn =
    pack.value.status === "issued"
      ? `"${pack.value.title}" has been issued to site. Delete it?\n\nIt's removed from the list but the record (and any sign-on) is kept.`
      : `Delete "${pack.value.title}"?\n\nIt's removed from the list but the record is kept.`;
  if (!window.confirm(warn)) return;
  deleting.value = true;
  try {
    await deleteWorkPack(id.value);
    await queryClient.invalidateQueries({ queryKey: ["work-packs"] });
    await router.push({
      name: "work-packs",
      params: { projectId: route.params.projectId },
    });
  } finally {
    deleting.value = false;
  }
}

// Issue now lives in the Pack control panel: a pack is issued only once the
// PIC has verified and the RM has authorised it (server-enforced).
const statusLabel = computed(
  () => WORK_PACK_STATUS_LABEL[(pack.value?.status as WorkPackStatus) ?? "draft"] ?? pack.value?.status,
);
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'work-packs', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Work packs
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="pack"
      class="mt-4 grid max-w-3xl gap-6"
    >
      <!-- Header -->
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-neutral-900">
            {{ pack.title }}
          </h1>
          <p class="mt-1 text-sm text-neutral-500">
            {{ pack.project_name }}<span v-if="pack.reference"> · {{ pack.reference }}</span>
            <span v-if="pack.shift_date"> · shift {{ fmt(pack.shift_date) }}</span>
          </p>
          <p class="mt-1 text-xs text-neutral-400">
            <span v-if="attribution(pack.created_by_name, pack.created_at)">
              Created by {{ attribution(pack.created_by_name, pack.created_at) }}
            </span>
            <span v-if="attribution(pack.issued_by_name, pack.issued_at)">
              · Issued by {{ attribution(pack.issued_by_name, pack.issued_at) }}
            </span>
            <span v-if="attribution(pack.last_updated_by_name, pack.last_updated_at)">
              · Last edited by {{ attribution(pack.last_updated_by_name, pack.last_updated_at) }}
            </span>
          </p>
        </div>
        <div class="flex shrink-0 flex-col items-end gap-2">
          <span
            class="rounded-full px-3 py-1 text-xs font-semibold"
            :class="pack.status === 'issued' || pack.status === 'authorised'
              ? 'bg-semantic-success/10 text-semantic-success'
              : pack.status === 'verified'
                ? 'bg-brand-primary/10 text-brand-primary'
                : ready?.ready
                  ? 'bg-semantic-success/10 text-semantic-success'
                  : 'bg-semantic-warning/10 text-semantic-warning'"
          >
            {{ pack.status === "issued"
              ? "Issued " + fmt(pack.issued_at)
              : pack.status === "draft"
                ? ready?.ready
                  ? "Ready to verify"
                  : `${ready?.requiredPresent}/${ready?.requiredTotal} required`
                : statusLabel }}
            · Rev {{ pack.revision }}
          </span>
          <a
            v-if="pack.status !== 'issued'"
            href="#wp-control"
            class="text-xs text-brand-primary hover:underline"
          >
            {{ pack.status === "draft" ? "Verify & authorise ↓" : pack.status === "verified" ? "Authorise ↓" : "Issue to site ↓" }}
          </a>
        </div>
      </header>

      <!-- Pack control: 019 roles, can't-skip reviews, verify / authorise / issue -->
      <WorkPackControl
        :pack="pack"
        @changed="refresh"
      />

      <!-- Template checklist (when the pack has a task type) -->
      <section
        v-if="ready && ready.mode === 'template' && ready.items.length"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-5"
      >
        <h2 class="mb-1 font-semibold">
          Required for {{ pack.task_type }}
        </h2>
        <p class="mb-3 text-sm text-neutral-400">
          The documents this task should include — tick them off as you attach.
        </p>
        <ul class="grid gap-1.5">
          <li
            v-for="item in ready.items"
            :key="item.kind + item.label"
            class="flex items-center gap-2 text-sm"
          >
            <span
              class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px]"
              :class="item.present
                ? 'bg-semantic-success/15 text-semantic-success'
                : 'border border-neutral-300 text-neutral-300'"
            >{{ item.present ? "✓" : "" }}</span>
            <span :class="item.present ? 'text-neutral-700' : 'text-neutral-500'">
              {{ item.label }}
            </span>
            <button
              v-if="!item.present && pack.status !== 'issued'"
              type="button"
              class="text-xs text-brand-primary hover:underline"
              @click="openAddForKind(item.kind)"
            >
              Add
            </button>
          </li>
        </ul>
      </section>

      <!-- Construction brief (planning: overview / scope / requirements / risks) -->
      <section>
        <h2 class="mb-1 text-lg font-semibold text-neutral-900">
          Construction brief
        </h2>
        <p class="mb-3 text-sm text-neutral-500">
          The plan for this work — overview &amp; objectives, scope &amp;
          boundaries, requirements &amp; constraints, risks &amp; governance.
        </p>
        <WorkPackConstructionBrief
          id="construction-brief"
          ref="briefRef"
          :pack="pack"
          :project="project"
          :prior-packs="priorPacks"
          @saved="refresh"
        />
      </section>

      <!-- Task Brief (structured pre-shift briefing) -->
      <div
        id="wp-sec-task_brief"
        class="scroll-mt-4"
      >
        <WorkPackTaskBrief
          :pack="pack"
          :templates="templates"
          :project="project"
          @saved="refresh"
        />
      </div>

      <!-- Where on the railway (ELR / mileage / line + map pin) -->
      <WorkPackLocation
        :pack="pack"
        :project="project"
        @saved="refresh"
      />

      <!-- Site briefing & sign-on (links to the staff_briefing record) -->
      <WorkPackBriefingLink :pack="pack" />

      <!-- Sections -->
      <section
        v-for="s in SECTIONS"
        :id="'wp-sec-' + s.key"
        :key="s.key"
        class="scroll-mt-4 rounded-xl border border-neutral-200 bg-neutral-0 p-5"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2 class="flex items-center gap-2 font-semibold">
              {{ s.label }}
              <span
                v-if="s.required"
                class="rounded px-1.5 py-0.5 text-[10px] font-bold"
                :class="sectionDocs(pack, s).length
                  ? 'bg-semantic-success/10 text-semantic-success'
                  : 'bg-semantic-warning/10 text-semantic-warning'"
              >{{ sectionDocs(pack, s).length ? "✓" : "Required" }}</span>
            </h2>
            <p class="text-sm text-neutral-400">
              {{ s.blurb }}
            </p>
          </div>
          <button
            v-if="pack.status !== 'issued'"
            type="button"
            class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            @click="openAdd(s)"
          >
            + Add
          </button>
        </div>

        <!-- Structured Bill of Materials (materials section) -->
        <div
          v-if="s.key === 'materials'"
          class="mt-3"
        >
          <table
            v-if="pack.bom && pack.bom.length"
            class="w-full text-sm"
          >
            <thead>
              <tr class="text-left text-xs text-neutral-400">
                <th class="pb-1 font-medium">
                  Item
                </th>
                <th class="pb-1 font-medium">
                  Qty
                </th>
                <th class="pb-1 font-medium">
                  Unit
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="line in pack.bom"
                :key="line.item_id"
                class="border-t border-neutral-100"
              >
                <td class="py-1.5 text-neutral-900">
                  {{ line.description }}
                </td>
                <td class="py-1.5 tabular-nums text-neutral-700">
                  {{ line.quantity ?? "—" }}
                </td>
                <td class="py-1.5 text-neutral-500">
                  {{ line.unit || "—" }}
                </td>
                <td class="py-1.5 text-right">
                  <button
                    v-if="pack.status !== 'issued'"
                    type="button"
                    class="text-neutral-300 hover:text-semantic-danger"
                    title="Remove line"
                    @click="removeLine(line.item_id)"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p
            v-else
            class="text-sm text-neutral-400"
          >
            No materials listed yet.
          </p>

          <!-- Add a BOM line -->
          <div
            v-if="pack.status !== 'issued'"
            class="mt-3 flex flex-wrap items-end gap-2"
          >
            <input
              v-model="bomForm.description"
              type="text"
              placeholder="Item / material"
              class="min-w-[12rem] flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              @keyup.enter="addLine"
            >
            <input
              v-model="bomForm.quantity"
              type="number"
              min="0"
              step="any"
              placeholder="Qty"
              class="w-20 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              @keyup.enter="addLine"
            >
            <input
              v-model="bomForm.unit"
              type="text"
              placeholder="Unit"
              class="w-24 rounded-md border border-neutral-300 px-3 py-2 text-sm"
              @keyup.enter="addLine"
            >
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="bomBusy || !bomForm.description.trim()"
              @click="addLine"
            >
              Add line
            </button>
            <span
              v-if="bomError"
              class="text-sm text-semantic-danger"
            >{{ bomError }}</span>
          </div>
        </div>

        <ul class="mt-3 grid gap-1">
          <li
            v-for="doc in sectionDocs(pack, s)"
            :key="doc.document_id"
            class="flex items-center gap-2 text-sm"
          >
            <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-300" />
            <span class="text-neutral-400">{{ kindLabel(doc.kind) }} —</span>
            <span class="font-medium text-neutral-900">{{ doc.title }}</span>
            <span
              v-if="doc.location_ref"
              class="rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500"
            >📍 {{ doc.location_ref }}</span>
            <button
              v-if="doc.file_id"
              type="button"
              class="text-brand-primary hover:underline"
              @click="viewDocument(doc.file_id)"
            >
              View
            </button>
            <button
              v-if="doc.file_id && doc.kind !== 'drawing'"
              type="button"
              class="text-brand-primary hover:underline"
              title="Read this document and draft the construction brief from it"
              @click="draftBriefFrom(doc.document_id)"
            >
              ✨ Draft brief
            </button>
            <a
              v-else-if="doc.url"
              :href="doc.url"
              target="_blank"
              rel="noopener"
              class="text-brand-primary hover:underline"
            >Open link</a>
            <span
              v-if="doc.markup"
              class="text-xs text-neutral-400"
              :title="doc.marked_up_by_name ? 'Marked up by ' + doc.marked_up_by_name : 'Marked up'"
            >✎ marked up<span v-if="doc.marked_up_by_name"> · {{ doc.marked_up_by_name }}</span></span>
          </li>
          <li
            v-if="!sectionDocs(pack, s).length && s.key !== 'materials'"
            class="text-sm text-neutral-400"
          >
            Nothing here yet.
          </li>
        </ul>

        <!-- Add form (scoped to this section's kinds) -->
        <div
          v-if="activeSection === s.key"
          class="mt-3 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
        >
          <select
            v-model="docForm.kind"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option
              v-for="k in kindsFor(s)"
              :key="k.value"
              :value="k.value"
            >
              {{ k.label }}
            </option>
          </select>
          <input
            v-model="docForm.title"
            type="text"
            placeholder="Document title"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <input
            v-model="docForm.location"
            type="text"
            placeholder="Location / structure (optional) — e.g. Loc 47"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
          <label class="grid gap-1 text-xs text-neutral-500">
            <span>Upload a file (PDF, drawing, image)</span>
            <input
              type="file"
              class="text-sm"
              @change="onFile"
            >
          </label>
          <label class="grid gap-1 text-xs text-neutral-500">
            <span>…or paste a link</span>
            <input
              v-model="docForm.url"
              type="url"
              placeholder="https://…"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
          </label>
          <div class="flex items-center gap-3 sm:col-span-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="docBusy || !docForm.title.trim()"
              @click="attach"
            >
              {{ docBusy ? "Adding…" : "Add to pack" }}
            </button>
            <button
              type="button"
              class="text-sm text-neutral-500 hover:text-neutral-900"
              @click="activeSection = null"
            >
              Cancel
            </button>
            <span
              v-if="docError"
              class="text-sm text-semantic-danger"
            >{{ docError }}</span>
          </div>
        </div>
      </section>

      <!-- Audit trail (from the immutable event log) -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-5">
        <h2 class="mb-3 font-semibold">
          History
        </h2>
        <ol
          v-if="history && history.length"
          class="grid gap-2 text-sm"
        >
          <li
            v-for="h in history"
            :key="h.seq"
            class="flex flex-wrap items-baseline gap-x-2"
          >
            <span class="font-medium text-neutral-900">{{ h.label }}</span>
            <span class="text-neutral-500">{{ h.actor_name ?? "—" }}</span>
            <span class="text-neutral-400">· {{ fmtDateTime(h.at) }}</span>
          </li>
        </ol>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No history yet.
        </p>
      </section>

      <!-- Danger zone -->
      <div class="flex justify-end border-t border-neutral-200 pt-4">
        <button
          type="button"
          class="rounded-md px-3 py-1.5 text-sm font-medium text-semantic-danger hover:bg-semantic-danger/10 disabled:opacity-50"
          :disabled="deleting"
          @click="deletePack"
        >
          {{ deleting ? "Deleting…" : "Delete pack" }}
        </button>
      </div>
    </div>
  </PageLayout>
</template>
